/**
 * Periods, the way a plant and its accountant talk about them.
 *
 * Every screen used to carry its own `<input type="month">` and its own idea
 * of what "this month" meant. That is fine until somebody asks for the last
 * seven days, or for the financial year to date, and has to work out the
 * dates themselves and type them twice.
 *
 * Two rules shape what is here:
 *
 *  - **The financial year runs April to March.** This is an Indian
 *    manufacturer audited under the Companies Act; a January-to-December
 *    "year to date" is the wrong answer to every question anyone will ask of
 *    it, so it is not offered.
 *  - **A period is always a pair of dates.** Presets are a way of arriving at
 *    a pair, never a separate kind of thing — so a screen only ever deals in
 *    `from` and `to`, and "last quarter" needs no special case anywhere
 *    downstream.
 *
 * Dates are ISO `YYYY-MM-DD` strings throughout, which is what the API takes
 * and what sorts correctly as text. Everything is computed in local time: a
 * shift that ended at 11pm belongs to that day in the plant, not to the next
 * one in UTC.
 */

export type PeriodPreset =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "last3m"
  | "last6m"
  | "last12m"
  | "this_year"
  | "last_year"
  | "this_fy"
  | "fy_to_date"
  | "last_fy"
  | "shift_day"
  | "shift_night"
  | "last_shift"
  | "custom";

export type PeriodShift = "DAY" | "NIGHT";

export interface Period {
  preset: PeriodPreset;
  /** Inclusive, ISO YYYY-MM-DD. */
  from: string;
  /** Inclusive, ISO YYYY-MM-DD. */
  to: string;
  /** One shift only, on every day of the period. Absent means both. */
  shift?: PeriodShift;
}

/**
 * What a report's changes are measured against: the period just before, the
 * same dates a month (MoM) or a quarter (QoQ) earlier, or the same dates a
 * year earlier (YoY), which takes the season out of it.
 */
export type Compare = "prior" | "month" | "quarter" | "year";

export const COMPARES: Compare[] = ["prior", "month", "quarter", "year"];

export const COMPARE_LABELS: Record<Compare, { short: string; long: string }> = {
  prior: { short: "Previous", long: "Previous period" },
  month: { short: "MoM", long: "Month on month" },
  quarter: { short: "QoQ", long: "Quarter on quarter" },
  year: { short: "YoY", long: "Year on year" },
};

const COMPARE_MONTHS: Partial<Record<Compare, number>> = { month: 1, quarter: 3 };

/**
 * When each shift starts, on the plant's clock. The night shift belongs to
 * the business date it started on, so 02:00 on the 7th is the night shift of
 * the 6th. Only "Last shift" depends on these.
 */
export const SHIFT_START_HOUR: Record<PeriodShift, number> = { DAY: 8, NIGHT: 20 };

export const PRESET_LABELS: Record<PeriodPreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last7: "Last 7 days",
  last30: "Last 30 days",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_quarter: "Last quarter",
  last3m: "Last 3 months",
  last6m: "Last 6 months",
  last12m: "Last 12 months",
  this_year: "This year",
  last_year: "Last year",
  this_fy: "This financial year",
  fy_to_date: "Financial year to date",
  last_fy: "Last financial year",
  shift_day: "Today – day shift",
  shift_night: "Today – night shift",
  last_shift: "Last shift",
  custom: "Custom range",
};

/**
 * How the picker groups them. "Relative" periods end today; "calendar"
 * periods are whole months, quarters and financial years. "Recommended" is
 * the short list most people want most of the time, so it is the first thing
 * the eye lands on.
 */
export const PRESET_SECTIONS: { title: string; presets: PeriodPreset[] }[] = [
  { title: "Recommended", presets: ["last7", "this_month"] },
  {
    title: "Relative dates",
    presets: ["today", "yesterday", "last7", "last30"],
  },
  {
    title: "Calendar months",
    presets: [
      "this_month",
      "this_quarter",
      "this_year",
      "last_month",
      "last_quarter",
      "last3m",
      "last6m",
      "last12m",
      "last_year",
    ],
  },
  // The year the books are kept in. Kept apart from the calendar year so
  // nobody picks "This year" expecting April to March, or the reverse.
  {
    title: "Financial year",
    presets: ["this_fy", "fy_to_date", "last_fy"],
  },
];

/** Shown only on screens whose figures are recorded by shift. */
export const SHIFT_PRESETS: PeriodPreset[] = ["shift_day", "shift_night", "last_shift"];

function iso(d: Date): string {
  // Not `toISOString()`: that converts to UTC first, so anywhere east of
  // Greenwich the date silently goes back a day for half the evening. India
  // is UTC+5:30, so this would be wrong every day after 6:30pm.
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  // Day 0 of the next month is the last day of this one, leap years included.
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

/** The financial year a date falls in, named by the year it starts. */
export function financialYearStart(d: Date): Date {
  const year = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return new Date(year, 3, 1); // 1 April
}

/** "2026-27" — how every Indian statement is labelled. */
export function financialYearLabel(d: Date): string {
  const start = financialYearStart(d).getFullYear();
  return `${start}-${`${start + 1}`.slice(-2)}`;
}

/** The quarter a date falls in, as an April-anchored index 0..3. */
function quarterStart(d: Date): Date {
  const fyStart = financialYearStart(d);
  const monthsIn = (d.getFullYear() - fyStart.getFullYear()) * 12
    + d.getMonth() - fyStart.getMonth();
  const quarter = Math.floor(monthsIn / 3);
  return new Date(fyStart.getFullYear(), fyStart.getMonth() + quarter * 3, 1);
}

/**
 * Turn a preset into a pair of dates.
 *
 * `anchor` is "now" — passed in rather than read from the clock so this is
 * testable and so a screen can resolve a period relative to a date the user
 * chose. `previous` is the period immediately before, kept for the comparison
 * anchor every KPI card is required to carry (non-negotiable 8).
 */
export function resolvePeriod(
  preset: PeriodPreset,
  anchor: Date = new Date(),
  current?: Period
): Period {
  const today = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());

  switch (preset) {
    case "today":
      return { preset, from: iso(today), to: iso(today) };
    case "yesterday": {
      const d = addDays(today, -1);
      return { preset, from: iso(d), to: iso(d) };
    }
    case "last7":
      // Seven days ending today, today included — six back, not seven.
      return { preset, from: iso(addDays(today, -6)), to: iso(today) };
    case "last30":
      return { preset, from: iso(addDays(today, -29)), to: iso(today) };
    case "this_month":
      return {
        preset,
        from: iso(startOfMonth(today)),
        to: iso(endOfMonth(today)),
      };
    case "last_month": {
      const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return { preset, from: iso(startOfMonth(d)), to: iso(endOfMonth(d)) };
    }
    case "this_quarter": {
      const start = quarterStart(today);
      const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
      return { preset, from: iso(start), to: iso(end) };
    }
    case "last_quarter": {
      const thisStart = quarterStart(today);
      const start = new Date(thisStart.getFullYear(), thisStart.getMonth() - 3, 1);
      const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
      return { preset, from: iso(start), to: iso(end) };
    }
    case "last3m":
    case "last6m":
    case "last12m": {
      // Whole months, ending with the last complete one. The month that is
      // running is "This month"; folding a half-month into "the last three"
      // would make every comparison between them lopsided.
      const months = preset === "last3m" ? 3 : preset === "last6m" ? 6 : 12;
      const start = new Date(today.getFullYear(), today.getMonth() - months, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { preset, from: iso(start), to: iso(end) };
    }
    case "this_year":
      return {
        preset,
        from: iso(new Date(today.getFullYear(), 0, 1)),
        to: iso(new Date(today.getFullYear(), 11, 31)),
      };
    case "last_year":
      return {
        preset,
        from: iso(new Date(today.getFullYear() - 1, 0, 1)),
        to: iso(new Date(today.getFullYear() - 1, 11, 31)),
      };
    case "this_fy": {
      const start = financialYearStart(today);
      const end = new Date(start.getFullYear() + 1, 2, 31); // 31 March
      return { preset, from: iso(start), to: iso(end) };
    }
    case "fy_to_date":
      return { preset, from: iso(financialYearStart(today)), to: iso(today) };
    case "last_fy": {
      const start = financialYearStart(today);
      const from = new Date(start.getFullYear() - 1, 3, 1);
      const to = new Date(start.getFullYear(), 2, 31); // 31 March
      return { preset, from: iso(from), to: iso(to) };
    }
    case "shift_day":
      return { preset, from: iso(today), to: iso(today), shift: "DAY" };
    case "shift_night":
      return { preset, from: iso(today), to: iso(today), shift: "NIGHT" };
    case "last_shift": {
      // The shift that most recently finished. Between 08:00 and 20:00 that
      // is last night's; after 20:00 it is today's day shift; before 08:00
      // it is yesterday's day shift, as last night's is still running.
      const hour = anchor.getHours();
      if (hour >= SHIFT_START_HOUR.DAY && hour < SHIFT_START_HOUR.NIGHT) {
        const d = iso(addDays(today, -1));
        return { preset, from: d, to: d, shift: "NIGHT" };
      }
      const d = iso(hour >= SHIFT_START_HOUR.NIGHT ? today : addDays(today, -1));
      return { preset, from: d, to: d, shift: "DAY" };
    }
    case "custom":
    default:
      // Keep whatever dates were already on screen; switching to Custom is a
      // request to edit them, not to reset them.
      return {
        preset: "custom",
        from: current?.from ?? iso(startOfMonth(today)),
        to: current?.to ?? iso(today),
        shift: current?.shift,
      };
  }
}

/** The period immediately before this one, of the same length. */
export function previousPeriod(period: Period): Period {
  const from = new Date(`${period.from}T00:00:00`);
  const to = new Date(`${period.to}T00:00:00`);
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  return {
    preset: "custom",
    from: iso(addDays(from, -days)),
    to: iso(addDays(to, -days)),
  };
}

/**
 * How many whole calendar months a period spans, or null if it does not
 * start on a 1st and end on a month end.
 */
function wholeMonths(period: Period): number | null {
  const from = new Date(`${period.from}T00:00:00`);
  const to = new Date(`${period.to}T00:00:00`);
  if (from.getDate() !== 1 || to.getTime() !== endOfMonth(to).getTime()) {
    return null;
  }
  return (to.getFullYear() - from.getFullYear()) * 12
    + to.getMonth() - from.getMonth() + 1;
}

/**
 * The same-shaped period one step back (`-1`) or forward (`1`).
 *
 * Whole months step by months — October steps to September, not to the 31
 * days before it; a quarter steps to the quarter before. Anything else steps
 * by its own length in days. The server compares a board with the period
 * before it by the same rule.
 */
export function shiftPeriod(period: Period, step: -1 | 1): Period {
  const from = new Date(`${period.from}T00:00:00`);
  // One shift on one day steps a shift at a time: day, night, next day.
  if (period.shift && period.from === period.to) {
    const crossesDay = step > 0 ? period.shift === "NIGHT" : period.shift === "DAY";
    const d = crossesDay ? iso(addDays(from, step)) : period.from;
    const shift: PeriodShift = period.shift === "DAY" ? "NIGHT" : "DAY";
    return { preset: "custom", from: d, to: d, shift };
  }
  const months = wholeMonths(period);
  if (months !== null) {
    const start = new Date(from.getFullYear(), from.getMonth() + step * months, 1);
    const end = new Date(start.getFullYear(), start.getMonth() + months, 0);
    return { preset: "custom", from: iso(start), to: iso(end), shift: period.shift };
  }
  const to = new Date(`${period.to}T00:00:00`);
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  return {
    preset: "custom",
    from: iso(addDays(from, step * days)),
    to: iso(addDays(to, step * days)),
    shift: period.shift,
  };
}

/** The month a period sits in, for the endpoints that still take one. */
export function periodMonth(period: Period): string {
  return period.from.slice(0, 7);
}

/** A period covering exactly one calendar month, from a YYYY-MM string. */
export function fromMonth(month: string): Period {
  const [year, m] = month.split("-").map(Number);
  const start = new Date(year, m - 1, 1);
  return {
    preset: "custom",
    from: iso(start),
    to: iso(endOfMonth(start)),
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

function human(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/**
 * What the period says on the screen.
 *
 * A whole calendar month reads as the month, not as two dates — "June 2026"
 * rather than "1 Jun 2026 – 30 Jun 2026", because that is what somebody would
 * say out loud. Runs of whole months read the same way: "Jul – Sept 2026",
 * a whole April-to-March year reads as "FY 2026-27" and a whole calendar
 * year as "2026".
 */
export function describePeriod(period: Period): string {
  const dates = describeDates(period);
  return period.shift ? `${dates} · ${SHIFT_LABEL[period.shift]}` : dates;
}

const SHIFT_LABEL: Record<PeriodShift, string> = {
  DAY: "Day shift",
  NIGHT: "Night shift",
};

function describeDates(period: Period): string {
  if (period.from === period.to) return human(period.from);

  const from = new Date(`${period.from}T00:00:00`);
  const to = new Date(`${period.to}T00:00:00`);
  const months = wholeMonths(period);
  if (months === 1) return `${MONTHS[from.getMonth()]} ${from.getFullYear()}`;
  if (months === 12 && from.getMonth() === 3) {
    return `FY ${financialYearLabel(from)}`;
  }
  if (months === 12 && from.getMonth() === 0) return `${from.getFullYear()}`;
  if (months !== null) {
    return from.getFullYear() === to.getFullYear()
      ? `${MONTHS[from.getMonth()]} – ${MONTHS[to.getMonth()]} ${to.getFullYear()}`
      : `${MONTHS[from.getMonth()]} ${from.getFullYear()} – `
        + `${MONTHS[to.getMonth()]} ${to.getFullYear()}`;
  }

  return `${human(period.from)} – ${human(period.to)}`;
}

/**
 * The same, shortened for the hint beside each choice in the picker: the
 * year is dropped from day ranges when both ends share it.
 */
export function describeShort(period: Period): string {
  if (period.shift) {
    return `${describeShort({ ...period, shift: undefined })} · ${SHIFT_LABEL[period.shift]}`;
  }
  if (wholeMonths(period) !== null || period.from === period.to) {
    return describePeriod(period);
  }
  const [fy, fm, fd] = period.from.split("-").map(Number);
  const [ty, tm, td] = period.to.split("-").map(Number);
  if (fy !== ty) return describePeriod(period);
  return `${fd} ${MONTHS[fm - 1]} – ${td} ${MONTHS[tm - 1]}`;
}

/** "vs Sept 2026" — what a KPI's change is measured against. */
export function comparisonLabel(period: Period, compare: Compare = "prior"): string {
  return `vs ${describePeriod(previousForBoard(period, compare))}`;
}

/** The period a board compares with — the server's rule, mirrored. */
export function previousForBoard(period: Period, compare: Compare = "prior"): Period {
  if (compare === "year") {
    return {
      preset: "custom",
      from: yearBefore(period.from),
      to: yearBefore(period.to),
      shift: period.shift,
    };
  }
  const months = COMPARE_MONTHS[compare];
  if (months) {
    const toMonthEnd = period.to === iso(endOfMonth(new Date(`${period.to}T00:00:00`)));
    return {
      preset: "custom",
      from: monthsBefore(period.from, months, false),
      to: monthsBefore(period.to, months, toMonthEnd),
      shift: period.shift,
    };
  }
  return shiftPeriod(period, -1);
}

/** The same day `months` earlier, held to the end of a shorter month — the
 *  server's rule, mirrored. */
function monthsBefore(isoDate: string, months: number, toMonthEnd: boolean): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const last = new Date(y, m - 1 - months + 1, 0).getDate();
  return iso(new Date(y, m - 1 - months, toMonthEnd ? last : Math.min(d, last)));
}

function yearBefore(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  // 29 February has no twin a year earlier; the server uses the 28th too.
  const day = m === 2 && d === 29 ? 28 : d;
  return iso(new Date(y - 1, m - 1, day));
}

// -- Links ---------------------------------------------------------------------
//
// A period travels in the address bar, so a report can be bookmarked or sent
// to somebody and opens on the same dates. The dates are what is kept, not
// the preset: "This month" sent on the 30th should still show that month when
// it is opened on the 2nd.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function periodToParams(period: Period, compare: Compare,
                               params: URLSearchParams): void {
  params.set("from", period.from);
  params.set("to", period.to);
  params.set("p", period.preset);
  if (period.shift) params.set("shift", period.shift);
  else params.delete("shift");
  if (compare !== "prior") params.set("compare", compare);
  else params.delete("compare");
}

export function periodFromParams(
  params: URLSearchParams
): { period: Period; compare: Compare } | null {
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  if (!ISO_DATE.test(from) || !ISO_DATE.test(to) || from > to) return null;
  const preset = params.get("p") ?? "custom";
  const shift = params.get("shift");
  return {
    period: {
      preset: (preset in PRESET_LABELS ? preset : "custom") as PeriodPreset,
      from,
      to,
      shift: shift === "DAY" || shift === "NIGHT" ? shift : undefined,
    },
    compare: (COMPARES as string[]).includes(params.get("compare") ?? "")
      ? (params.get("compare") as Compare)
      : "prior",
  };
}

// -- As-on dates ---------------------------------------------------------------
//
// Stock and WIP are balances at a moment, not totals over a period, so they
// take one date. These are the dates people actually ask for them on.

export type AsOnPreset =
  | "today"
  | "yesterday"
  | "end_last_month"
  | "end_last_quarter"
  | "end_last_fy"
  | "end_last_year";

export const AS_ON_LABELS: Record<AsOnPreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  end_last_month: "End of last month",
  end_last_quarter: "End of last quarter",
  end_last_fy: "End of last financial year",
  end_last_year: "End of last year",
};

export function resolveAsOn(preset: AsOnPreset, anchor: Date = new Date()): string {
  const today = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  switch (preset) {
    case "today":
      return iso(today);
    case "yesterday":
      return iso(addDays(today, -1));
    case "end_last_month":
      return iso(new Date(today.getFullYear(), today.getMonth(), 0));
    case "end_last_quarter":
      return iso(addDays(quarterStart(today), -1));
    case "end_last_fy":
      return iso(addDays(financialYearStart(today), -1));
    case "end_last_year":
      return iso(new Date(today.getFullYear() - 1, 11, 31));
  }
}

/** "6 Oct 2026" — for the as-on button. */
export function describeDay(isoDate: string): string {
  return human(isoDate);
}

/** Today's date as the plant counts it. */
export function todayIso(anchor: Date = new Date()): string {
  return iso(anchor);
}

/** The default a screen opens on: the month that is running. */
export function defaultPeriod(anchor: Date = new Date()): Period {
  return resolvePeriod("this_month", anchor);
}
